// packages/degreeMaps/src/index.ts
import _minBy from "lodash/minBy";
import _maxBy from "lodash/maxBy";
import _isObject from "lodash/isObject";
import _isNumber from "lodash/isNumber";
import _isEmpty from "lodash/isEmpty";
import {userHasAnyAllowedRole} from "@shared/permissions/validators";
import {decamelize, capitalize} from "@shared/utils/string";
import {getDynamicRender} from "@shared/utils/dynamicString";
var {MIN_EFFECTIVE_START_DATE, MAX_EFFECTIVE_END_DATE} = require("@shared/constants/effectiveDating");
function degreeMapsUtils({
  courses = {},
  courseSets = [],
  effectiveDateRange: dateRanges,
  options = {
    useEffectiveDateQueryFix: false,
    useLatestRevisions: false
  },
  localize = (x) => x
} = {}) {
  const courseLabel = capitalize(localize("course"));
  function getCoursesByGroupId(courseId) {
    const coursesWithSameCourseGroupId = Object.values(courses || {}).filter((course) => (course.courseGroupId === courseId || course.deprecatedCourseGroupId === courseId) && doesCourseMeetEffectiveDatingRequirements(course)).sort((courseA, courseB) => courseA.effectiveStartDate < courseB.effectiveStartDate ? 1 : -1);
    return coursesWithSameCourseGroupId.length ? coursesWithSameCourseGroupId[0] : {};
  }
  function doesCourseMeetEffectiveDatingRequirements(course) {
    if (options?.useLatestRevisions)
      return true;
    const defaultRange = options.useEffectiveDateQueryFix ? MAX_EFFECTIVE_END_DATE : `${MIN_EFFECTIVE_START_DATE};${MAX_EFFECTIVE_END_DATE}`;
    let [startDate, endDate] = (dateRanges || defaultRange).split(";").flatMap((s) => s.split(","));
    endDate = endDate || startDate;
    const courseEffectiveStartDate = course.effectiveStartDate || startDate;
    const courseEffectiveEndDate = course.effectiveEndDate || endDate;
    if (options && options.useEffectiveDateQueryFix) {
      return (course.effectiveStartDate || startDate) <= startDate;
    }
    return courseEffectiveStartDate <= endDate && courseEffectiveEndDate >= startDate;
  }
  function getCourseSetById(id) {
    return courseSets.find((c) => c.id === id);
  }
  function getCourseSetNameById(id) {
    return (getCourseSetById(id) || {}).name;
  }
  function _normalizeMinMax({min, max, value}) {
    if (_isNumber(value))
      return {min: value, max: value};
    if (!_isNumber(min) && _isNumber(max))
      return {min: max, max};
    if (!_isNumber(max) && _isNumber(min))
      return {max: min, min};
    return {min, max};
  }
  function isFieldVisible(user, field) {
    if (!field)
      return false;
    return userHasAnyAllowedRole(user, null, field.rolesAllowedToSee);
  }
  function isFieldEditable(user, field) {
    if (!field)
      return false;
    return userHasAnyAllowedRole(user, null, field.rolesAllowedToEdit);
  }
  function getCourseCodeAndName(courseId) {
    const course = getCoursesByGroupId(courseId);
    if (Object.keys(course).length) {
      const code = course.code || `${course.subjectCode + course.courseNumber}`;
      let name = course.longName || course.name || `Untitled ${courseLabel}`;
      const appendRequestStatus = ["pending", "suspended", "rejected"].includes(course.requestStatus);
      if (appendRequestStatus) {
        name = `${name} (${course.requestStatus} in workflow)`;
      } else if (course.status === "Inactive") {
        name = `${name} (Inactive)`;
      }
      return {code, name};
    }
    return null;
  }
  function getCourseName(courseId, options2) {
    if (options2?.titleInterpolation) {
      const course = getCoursesByGroupId(courseId);
      if (!_isEmpty(course)) {
        const interpolatedLabel = getDynamicRender(options2.titleInterpolation, course, {useCustomFields: true, fallbackToPattern: false});
        if (["pending", "suspended", "rejected"].includes(course.requestStatus)) {
          return `${interpolatedLabel} (${course.requestStatus} in workflow)`;
        }
        if (course.status === "Inactive") {
          return `${interpolatedLabel} (Inactive)`;
        }
        return interpolatedLabel;
      }
    }
    const courseCodeAndName = getCourseCodeAndName(courseId);
    if (!courseCodeAndName) {
      if (options2?.displayRequisiteIdInlineIfEntityNotFound)
        return courseId;
      return "";
    }
    return `${courseCodeAndName.code} - ${courseCodeAndName.name}`;
  }
  function getRequirementGroups(requirement, resources = []) {
    const requirementGroups = (requirement.requirementSelect || []).reduce((acc, item) => {
      if (item.type === "courses" || item.type === "course") {
        const {requirementGroup = null} = getCoursesByGroupId(item.value);
        if (requirementGroup)
          acc.push(requirementGroup);
      } else if (item.type === "courseSets") {
        const courseSet = getCourseSetById(item.value);
        if (courseSet && courseSet.requirementGroups)
          acc.push(...courseSet.requirementGroups);
      }
      return acc;
    }, []);
    return Array.from(new Set(requirementGroups)).map((id) => {
      const res = resources.find((r) => r.id === id) || {};
      return res.displayName || id;
    }).join(", ") || null;
  }
  function getRequirementCreditRange(creditValues) {
    let creditRange = {};
    if (Array.isArray(creditValues)) {
      const {min} = _minBy(creditValues.filter((v) => _isNumber(v.min)), (c) => c.min) || {};
      const {max} = _maxBy(creditValues.filter((v) => _isNumber(v.max)), (c) => c.max) || {};
      Object.assign(creditRange, {min, max});
    } else {
      creditRange = creditValues;
    }
    return creditRange;
  }
  function getRequirementCreditRangeAsText(creditValues) {
    const minMax = getRequirementCreditRange(creditValues);
    return outputCreditRange(minMax);
  }
  function getTotalCreditsOutput(requirements, creditType, creditKey) {
    const totalCredits = requirements.reduce((acc, r) => {
      const creditValues = getRequirementCreditValues(r, creditType, creditKey);
      const creditRange = getRequirementCreditRange(creditValues);
      acc.min += creditRange.min || 0;
      acc.max += creditRange.max || 0;
      return acc;
    }, {min: 0, max: 0});
    return outputCreditRange(totalCredits);
  }
  function outputCreditRange({min, max}) {
    return min === max ? min : [min, max].filter((v) => _isNumber(v)).join(" - ") || "";
  }
  function getCourseCreditMinMaxValue(courseId, creditType) {
    const course = getCoursesByGroupId(courseId);
    const {min, max, value} = course && course.credits && course.credits[creditType] || {};
    return _normalizeMinMax({min, max, value});
  }
  function getCourseSetCreditMinMaxValue(setId, creditType) {
    const courseSet = getCourseSetById(setId);
    const {min, max, value} = courseSet && courseSet.credits && courseSet.credits[creditType] || {};
    return _normalizeMinMax({min, max, value});
  }
  function getRequirementCreditValues(requirement, creditType, creditKey) {
    if (_isObject(requirement[creditKey]))
      return _normalizeMinMax(requirement[creditKey]);
    const creditRange = {};
    (requirement.requirementSelect || []).forEach((item) => {
      if (item.type === "courses" || item.type === "course" || item.type === "courseSets") {
        const normalizeMinMax = item.type === "courseSets" ? getCourseSetCreditMinMaxValue(item.value, creditType) : getCourseCreditMinMaxValue(item.value, creditType);
        if (_isNumber(normalizeMinMax.min)) {
          creditRange.min = Math.min(creditRange.min || Number.POSITIVE_INFINITY, normalizeMinMax.min);
        }
        if (_isNumber(normalizeMinMax.max)) {
          creditRange.max = Math.max(creditRange.max || Number.NEGATIVE_INFINITY, normalizeMinMax.max);
        }
      }
    });
    return creditRange;
  }
  function getAllRequirements(semesters) {
    return semesters.reduce((acc, sem) => {
      acc.push(...sem.requirements);
      return acc;
    }, []);
  }
  function getTotalGeneralEducationCredits(allRequirements) {
    return getTotalCreditsOutput(allRequirements.filter((r) => r.area === "generalEducation"), "creditHours", "actualCredits");
  }
  function getTotalMajorCredits(allRequirements) {
    return getTotalCreditsOutput(allRequirements.filter((r) => r.area === "major"), "creditHours", "actualCredits");
  }
  function getTotalMinorCredits(allRequirements) {
    return getTotalCreditsOutput(allRequirements.filter((r) => r.area === "minor"), "creditHours", "actualCredits");
  }
  function getTotalElectiveCredits(allRequirements) {
    return getTotalCreditsOutput(allRequirements.filter((r) => r.area === "elective"), "creditHours", "actualCredits");
  }
  function getTotalActualCredits(allRequirements) {
    return getTotalCreditsOutput(allRequirements, "creditHours", "actualCredits");
  }
  function getTotalActualCreditsForSemester(semesters, semesterIndex) {
    return getTotalCreditsOutput(semesters[semesterIndex].requirements, "creditHours", "actualCredits");
  }
  function getTotalProgressCreditsForSemester(semesters, semesterIndex) {
    return getTotalCreditsOutput(semesters[semesterIndex].requirements, "academicProgressHours", "progressCredits");
  }
  function getCoursesOutput(value) {
    function requirementToName(req) {
      switch (req.type) {
        case "course":
        case "courses":
          return getCourseName(req.value);
        case "requirementDesignation":
          return req.value;
        default:
          return decamelize(req.value);
      }
    }
    return (value || []).map(requirementToName).filter(Boolean).join(", ");
  }
  return {
    isFieldVisible,
    isFieldEditable,
    getCourseName,
    getCourseCodeAndName,
    getRequirementGroups,
    getRequirementCreditRange,
    getRequirementCreditRangeAsText,
    getTotalCreditsOutput,
    outputCreditRange,
    getRequirementCreditValues,
    getAllRequirements,
    getTotalGeneralEducationCredits,
    getTotalMajorCredits,
    getTotalMinorCredits,
    getTotalElectiveCredits,
    getTotalActualCredits,
    getTotalActualCreditsForSemester,
    getTotalProgressCreditsForSemester,
    getCoursesOutput,
    getCoursesByGroupId,
    getCourseCreditMinMaxValue,
    getCourseSetCreditMinMaxValue,
    getCourseSetById,
    getCourseSetNameById,
    doesCourseMeetEffectiveDatingRequirements
  };
}
export {
  degreeMapsUtils
};
